/**
 * PATH: apps/qa/src/components/inspection/Tooltip.tsx
 * PURPOSE: Reusable tooltip component using React Portal for proper z-index handling
 * 
 * WHY: Tooltips need to render above all other elements and cannot be constrained
 * by parent overflow or z-index contexts. Using a portal ensures proper layering.
 * 
 * FEATURES:
 * - Renders above all elements via React Portal
 * - Smooth fade-in animation
 * - Auto-positions above trigger element
 * - Accessible with keyboard focus support
 * - Cleanup on unmount to prevent memory leaks
 */

import { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { cn } from '@/lib/utils'

interface TooltipProps {
  children: React.ReactNode
  content: React.ReactNode
  className?: string
  delay?: number
}

export function Tooltip({ children, content, className, delay = 150 }: TooltipProps) {
  const [show, setShow] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    // Small delay to prevent flicker
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        setCoords({
          top: rect.top + window.scrollY - 8,
          left: rect.left + window.scrollX + rect.width / 2,
        })
      }
      setShow(true)
    }, delay)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setShow(false)
  }

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        {children}
      </div>
      {show && ReactDOM.createPortal(
        <div
          className={cn(
            'fixed z-50 px-4 py-3 text-sm bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-xl shadow-2xl',
            'max-w-sm transform -translate-x-1/2 -translate-y-full pointer-events-none',
            'animate-in fade-in-0 zoom-in-95 duration-150',
            className
          )}
          style={{ 
            top: coords.top,
            left: coords.left,
          }}
          role="tooltip"
        >
          {content}
          {/* Arrow pointing down */}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-slate-900 dark:border-t-slate-100" />
        </div>,
        document.body
      )}
    </div>
  )
}

export default Tooltip
